import { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import Button from '../ui/Button';
import FormModal, { Field, inputClass } from '../fieldWork/FormModal';
import labourService from '../../services/labour';
import fieldWorkService from '../../services/fieldWork';
import { errorMessage } from '../../utils/apiResponse';
import { getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import {
  type ActivityType,
  type LabourProvider,
  type PayBasis,
  type WorkerType,
} from '../../types/fieldWork';
import { PAY_BASIS_LABELS, type LabourSettings, type Rate, type RateAppliesTo, type RateUnit } from '../../types/labour';
import { Notice } from './shared';
import { money } from './labourUtils';

const UNITS: RateUnit[] = ['trees', 'rows', 'bins', 'kg', 'tonnes', 'loads', 'bales', 'hectares'];

const WHO_LABEL: Record<WorkerType, string> = {
  employee: 'Our employees',
  casual: 'Our casuals',
  contractor_worker: 'Contractors (no provider set)',
};

function who(r: Rate): string {
  if (r.applies_to === 'labour_provider') return r.labour_provider_name ?? 'Provider';
  return WHO_LABEL[r.worker_type as WorkerType] ?? String(r.worker_type);
}

/**
 * Cost rates — what an hour (or a bin, a tree, a hectare, a job) of labour
 * costs the business, for budgets and job costing. Not payroll: set per kind
 * of worker or per labour provider, never per person, so nobody reads what an
 * individual is paid. The org sets its own figures; nothing is checked against
 * a minimum wage or award.
 */
export default function RatesPanel({ settings }: { settings: LabourSettings }) {
  const tz = useOrgTimezone();
  const [rates, setRates] = useState<Rate[]>([]);
  const [showEnded, setShowEnded] = useState(false);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRates(await labourService.getRates(showEnded));
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load rates'));
    }
  }, [showEnded]);
  useEffect(() => { load(); }, [load]);

  const today = getTodayInTz(tz);
  const tax = settings.tax_name || 'tax';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={showEnded} onChange={e => setShowEnded(e.target.checked)}
            className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
          <span className="text-neutral-600">Show ended rates</span>
        </label>
        <Button size="sm" variant="primary" onClick={() => setAdding(true)}><Plus className="h-4 w-4" /> Add rate</Button>
      </div>
      <p className="text-sm text-neutral-500">
        What labour costs you, for budgets and job costing — for example a contractor’s all-in hourly charge as it
        appears on their invoice, or the hourly figure you want to budget for your own staff. Set per kind of worker or
        per labour provider, never per person.
      </p>
      {error && <Notice tone="error">{error}</Notice>}
      {rates.length === 0 ? (
        <p className="text-sm text-neutral-500 py-6 text-center">No rates yet — labour shows as hours only until you add some.</p>
      ) : (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg" aria-label="Rates">
          {rates.map(r => {
            const ended = !!r.effective_to && r.effective_to < today;
            return (
              <li key={r.id} className={`px-3 py-3 flex flex-wrap items-start gap-3 ${ended ? 'bg-neutral-50' : ''}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-neutral-900">
                    <span className="font-medium">{who(r)}</span>
                    {' · '}{money(r.amount, settings.currency)} {r.basis === 'per_unit' && r.unit ? `per ${r.unit.replace(/s$/, '')}` : PAY_BASIS_LABELS[r.basis]}
                    <span className="text-xs text-neutral-500"> {r.includes_tax ? 'inc' : 'ex'} {tax}</span>
                  </p>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {[r.activity_type_name, r.farm_name, r.cultivar_name].filter(Boolean).join(' · ') || 'All work'}
                    {' · from '}{r.effective_from}{r.effective_to ? ` to ${r.effective_to}` : ''}
                  </p>
                </div>
                {!r.effective_to && (
                  <button type="button" className="text-xs text-indigo-600 hover:underline"
                    onClick={() => labourService.endRate(r.id, today).then(load).catch(e => setError(errorMessage(e, 'Could not end the rate')))}>
                    End today
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {adding && <RateModal taxName={tax} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} today={today} />}
    </div>
  );
}

function RateModal({ onClose, onSaved, today, taxName }: { onClose: () => void; onSaved: () => void; today: string; taxName: string }) {
  const [appliesTo, setAppliesTo] = useState<RateAppliesTo>('labour_provider');
  const [workerType, setWorkerType] = useState<WorkerType>('employee');
  const [providerId, setProviderId] = useState<number | ''>('');
  const [activityId, setActivityId] = useState<number | ''>('');
  const [basis, setBasis] = useState<PayBasis>('per_hour');
  const [unit, setUnit] = useState<RateUnit>('bins');
  const [amount, setAmount] = useState('');
  const [includesTax, setIncludesTax] = useState(true);
  const [from, setFrom] = useState(today);
  const [providers, setProviders] = useState<LabourProvider[]>([]);
  const [activities, setActivities] = useState<ActivityType[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fieldWorkService.getProviders().then(p => {
      setProviders(p);
      if (!p.length) setAppliesTo('worker_type');
    }).catch(() => setAppliesTo('worker_type'));
    fieldWorkService.getActivityTypes().then(setActivities).catch(() => {});
  }, []);

  // Contractors bill including tax; your own staff's cost has none.
  useEffect(() => {
    setIncludesTax(appliesTo === 'labour_provider' || workerType === 'contractor_worker');
  }, [appliesTo, workerType]);

  const valid = amount !== '' && Number(amount) >= 0 && !!from && (appliesTo !== 'labour_provider' || !!providerId);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await labourService.createRate({
        applies_to: appliesTo,
        ...(appliesTo === 'worker_type' ? { worker_type: workerType } : {}),
        ...(appliesTo === 'labour_provider' ? { labour_provider_id: Number(providerId) } : {}),
        activity_type_id: activityId ? Number(activityId) : null,
        basis,
        unit: basis === 'per_unit' ? unit : null,
        amount: Number(amount),
        includes_tax: includesTax,
        effective_from: from,
      });
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Could not add the rate'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal title="Add rate" onClose={onClose} onSubmit={submit} submitting={saving} submitDisabled={!valid} error={error}>
      <Field label="Whose labour" htmlFor="rate-for">
        <select id="rate-for" className={inputClass} value={appliesTo} onChange={e => setAppliesTo(e.target.value as RateAppliesTo)}>
          <option value="labour_provider">A labour provider</option>
          <option value="worker_type">Our own staff, or contractors with no provider</option>
        </select>
      </Field>
      {appliesTo === 'worker_type' && (
        <Field label="Which" htmlFor="rate-wtype">
          <select id="rate-wtype" className={inputClass} value={workerType} onChange={e => setWorkerType(e.target.value as WorkerType)}>
            {(Object.keys(WHO_LABEL) as WorkerType[]).map(t => <option key={t} value={t}>{WHO_LABEL[t]}</option>)}
          </select>
        </Field>
      )}
      {appliesTo === 'labour_provider' && (
        <Field label="Provider" htmlFor="rate-provider">
          <select id="rate-provider" className={inputClass} value={providerId} onChange={e => setProviderId(e.target.value ? Number(e.target.value) : '')}>
            <option value="">Choose…</option>
            {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
      )}
      <Field label="For which work" htmlFor="rate-activity">
        <select id="rate-activity" className={inputClass} value={activityId} onChange={e => setActivityId(e.target.value ? Number(e.target.value) : '')}>
          <option value="">All work</option>
          {activities.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Charged" htmlFor="rate-basis">
          <select id="rate-basis" className={inputClass} value={basis} onChange={e => setBasis(e.target.value as PayBasis)}>
            {(Object.keys(PAY_BASIS_LABELS) as PayBasis[]).map(b => <option key={b} value={b}>{PAY_BASIS_LABELS[b]}</option>)}
          </select>
        </Field>
        {basis === 'per_unit' && (
          <Field label="Unit" htmlFor="rate-unit">
            <select id="rate-unit" className={inputClass} value={unit} onChange={e => setUnit(e.target.value as RateUnit)}>
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </Field>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={`Amount ${includesTax ? 'inc' : 'ex'} ${taxName}`} htmlFor="rate-amount">
          <input id="rate-amount" type="number" inputMode="decimal" min={0} step="0.01" className={inputClass} value={amount} onChange={e => setAmount(e.target.value)} />
        </Field>
        <Field label="From" htmlFor="rate-from" hint="A live rate for the same thing ends the day before.">
          <input id="rate-from" type="date" className={inputClass} value={from} onChange={e => setFrom(e.target.value)} />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={includesTax} onChange={e => setIncludesTax(e.target.checked)}
          className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
        <span className="text-neutral-700">This amount includes {taxName} (as on the invoice)</span>
      </label>
    </FormModal>
  );
}
